import { AssignmentInd, CardGiftcard, CurrencyExchange, EmojiEvents, Flag, Groups3 } from "@mui/icons-material";
import BrushIcon from "@mui/icons-material/Brush";
import DiscountIcon from "@mui/icons-material/Discount";
import AssessmentIcon from "@mui/icons-material/Assessment";
import EmailIcon from "@mui/icons-material/Email";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import EuroIcon from "@mui/icons-material/Euro";
import ExtensionIcon from "@mui/icons-material/Extension";
import LanguageIcon from "@mui/icons-material/Language";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import KeyIcon from "@mui/icons-material/Key";
import LocalMallIcon from "@mui/icons-material/LocalMall";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import People from "@mui/icons-material/People";
import PageviewIcon from "@mui/icons-material/Pageview";
import PercentIcon from "@mui/icons-material/Percent";
import RecommendIcon from "@mui/icons-material/Recommend";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import SendAndArchiveTwoTone from "@mui/icons-material/SendAndArchiveTwoTone";
import { StateMachineProvider as OrigStateMachineProvider } from "little-state-machine";
import * as React from "react";
import { Admin, Authenticated, CustomRoutes, Resource, defaultTheme } from "react-admin";
import { BrowserRouter, Route } from "react-router-dom";
import BasketProvider from "./basket/basketProvider";
import { createGlobalStore } from "./globalState";
import { i18nProvider } from "./i18n";
import {
  GeoffreyProductRecommendationsCreate,
  GeoffreyProductRecommendationsEdit,
  GeoffreyProductRecommendationsList,
} from "./list/geoffreyProductRecommendations";

import { AppPuzzleCreate, AppPuzzleList } from "./list/appPuzzle";
import { AppRedirectLinkCreate, AppRedirectLinkEdit, AppRedirectLinkList } from "./list/appRedirectLink";
import { CancellationCouponCreate, CancellationCouponEdit, CancellationCouponList } from "./list/cancellationCoupon";
import {
  EmailProductDetailTextCreate,
  EmailProductDetailTextEdit,
  EmailProductDetailTextList,
} from "./list/emailProducts";
import { EmailTemplatesCreate, EmailTemplatesEdit, EmailTemplatesList } from "./list/emailTemplates";
import { FeatureFlagsEdit, FeatureFlagsList } from "./list/featureFlag";
import { FreeSubscriptionCreate, FreeSubscriptionList } from "./list/freeSubscriptions";
import { InternalUserEdit, InternalUserList, InternalUserShow } from "./list/internalUser";
import {
  InternalUserGroupCreate,
  InternalUserGroupEdit,
  InternalUserGroupList,
  InternalUserGroupShow,
} from "./list/internalUserGroup";
import { LocaleCreate, LocaleEdit, LocaleShow, LocalesList } from "./list/locales";
import { NewBundleCreate, NewBundleEdit, NewBundleList } from "./list/newBundle";
import { NewProductList, NewProductsShow, ProductCreate, ProductEdit } from "./list/newProduct";
import { OrderEdit, OrderList } from "./list/order";
import Order from "./list/orders";
import { PriceEdit, PriceList } from "./list/price";
import { ProductTagsCreate, ProductTagsEdit, ProductTagsList } from "./list/productTags";
import { PromotionCodeCreate, PromotionCodeEdit, PromotionCodeList } from "./list/promotionCode";
import ShopifyOrdersList from "./list/shopifyOrders";
import { SubscriptionList } from "./list/subscriptions";
import UsersList from "./list/users";
import { SidebarLayout } from "./menu/layout";
import SSOAuthProvider from "./providers/SSOAuthProvider";
import grapheneDataProvider from "./providers/grapheneDataProvider2";
import Basket from "./screens/basket";
import Checkout from "./screens/checkout";
import Dashboard from "./screens/dashboard";
import InvoiceArchiveList from "./screens/invoicesArchive";
import { SSOLogin, SSOLoginCallback } from "./screens/login";
import { OrderCharts } from "./screens/orderCharts";
import Settings from "./screens/settings";
import ShopifyOrderDetail from "./screens/shopifyOrderDetail";
import ThankYou from "./screens/thankYou";
import UserDetail from "./screens/userDetail";
import { GeoffreyRecommendationTextsEdit, GeoffreyRecommendationTextsList } from "./list/geoffreyRecommendedTexts";
import { OrderTesterCreate, OrderTesterEdit, OrderTesterList } from "./list/orderTesters";
import { ChallengeCreate, ChallengeEdit, ChallengeList } from "./list/challenge";
import { MouthscanAnalysisResultShow, MouthscanAnalysisResultsList } from "./list/mouthscanAnalysisResults";
import { JourneyList, JourneyCreate, JourneyEdit } from "./list/journey";
import { PdpGroupCreate, PdpGroupEdit, PdpGroupList } from "./list/pdpGroup";
import SunstarRedirectUrls, { SunstarRedirectUrlsEdit, SunstarRedirectUrlsShow } from "./list/SunstarRedirectUrls";
import { MessageCreate, MessageEdit, MessageList, MessageShow } from "./list/firebase_notification";
import {
  NotificationSettingsEdit,
  NotificationSettingsList,
  NotificationSettingsShow,
} from "./list/notificationSettings";
import {
  TranslationKeyCreate,
  TranslationKeyEdit,
  TranslationKeyList,
  TranslationKeyShow,
} from "./list/mouthcheckTranslationKeyList";
const StateMachineProvider: React.FC<{ children: React.ReactNode }> = OrigStateMachineProvider;

createGlobalStore();

const theme = {
  ...defaultTheme,
};

const App = () => (
  <BrowserRouter>
    <StateMachineProvider>
      <BasketProvider>
        <Admin
          dashboard={Dashboard}
          layout={SidebarLayout}
          dataProvider={grapheneDataProvider}
          loginPage={SSOLogin}
          authProvider={SSOAuthProvider}
          i18nProvider={i18nProvider}
          disableTelemetry
          theme={theme}
        >
          <Resource
            name="orders"
            list={OrderList}
            edit={OrderEdit}
            icon={RequestQuoteIcon}
            options={{ label: "Orders" }}
          />
          <Resource
            name="orderTesters"
            list={OrderTesterList}
            edit={OrderTesterEdit}
            create={OrderTesterCreate}
            options={{ label: "Order Testers" }}
            icon={ChatBubbleIcon}
            hasShow={false}
          />
          <Resource
            name="shipping"
            list={Order.list}
            // edit={OrderEdit}
            icon={Order.icon}
            options={{ label: "Shipping" }}
          />

          <Resource
            name="v2Products"
            list={<NewProductList hasEdit={false} />}
            show={NewProductsShow}
            options={{ label: "Products v2" }}
            icon={BrushIcon}
            recordRepresentation={(record) => record.name}
            hasEdit={false}
          />
          <Resource
            name="v2Productpres"
            list={<NewProductList hasEdit={true} hasCreate={true} />}
            edit={ProductEdit}
            create={ProductCreate}
            options={{ label: "Products Pre v2" }}
            icon={BrushIcon}
            recordRepresentation={(record) => record.name}
            hasShow={false}
          />
          <Resource
            name="productTags"
            list={ProductTagsList}
            create={ProductTagsCreate}
            edit={ProductTagsEdit}
            icon={LocalOfferIcon}
            options={{ label: "Product Tags" }}
          />
          <Resource name="prices" list={PriceList} edit={PriceEdit} icon={EuroIcon} options={{ label: "Prices" }} />
          <Resource
            name="pdpGroup"
            list={PdpGroupList}
            edit={PdpGroupEdit}
            create={PdpGroupCreate}
            icon={PageviewIcon}
            options={{ label: "PDP Groups" }}
          />

          <Resource
            name="v2Bundles"
            list={<NewBundleList />}
            show={<NewBundleEdit hasSave={false} />}
            icon={LibraryBooksIcon}
            options={{ label: "Bundles v2" }}
            hasEdit={false}
          />
          <Resource
            name="v2Bundlepres"
            list={<NewBundleList showCreate={true} />}
            edit={<NewBundleEdit hasSave={true} />}
            create={NewBundleCreate}
            icon={LibraryBooksIcon}
            options={{ label: "Bundles Pre v2" }}
            hasShow={false}
          />
          <Resource
            name="promotionCode"
            list={PromotionCodeList}
            edit={PromotionCodeEdit}
            show={PromotionCodeEdit}
            create={PromotionCodeCreate}
            icon={PercentIcon}
            options={{ label: "Promotion Code" }}
          />
          <Resource
            name="appPuzzle"
            list={AppPuzzleList}
            create={AppPuzzleCreate}
            icon={ExtensionIcon}
            options={{ label: "App Puzzle" }}
          />
          <Resource
            name="appRedirectLink"
            list={AppRedirectLinkList}
            create={AppRedirectLinkCreate}
            edit={AppRedirectLinkEdit}
            icon={OpenInNewIcon}
            options={{ label: "App Redirect Link" }}
          />
          <Resource
            name="geoffreyRecommendationTexts"
            list={GeoffreyRecommendationTextsList}
            edit={GeoffreyRecommendationTextsEdit}
            options={{ label: "Geoffrey Texts" }}
            icon={ChatBubbleIcon}
            hasShow={false}
            hasCreate={false}
          />
          <Resource
            name="challenge"
            list={ChallengeList}
            create={ChallengeCreate}
            edit={ChallengeEdit}
            options={{ label: "Challenge" }}
            icon={EmojiEvents}
          />
          <Resource
            name="journey"
            list={JourneyList}
            create={JourneyCreate}
            edit={JourneyEdit}
            options={{ label: "Journey" }}
            icon={EmojiEvents}
          />
          <Resource
            name="geoffreyProductRecommendations"
            list={GeoffreyProductRecommendationsList}
            edit={GeoffreyProductRecommendationsEdit}
            create={GeoffreyProductRecommendationsCreate}
            icon={RecommendIcon}
            options={{ label: "Geoffrey Product Recommendations" }}
          />
          <Resource
            name="cancellationCoupon"
            list={CancellationCouponList}
            create={CancellationCouponCreate}
            edit={CancellationCouponEdit}
            icon={DiscountIcon}
            options={{ label: "Cancellation Coupon" }}
          />

          {/* <Resource
            name="referralCodeIndividual"
            list={ReferralCodeListIndividual}
            edit={ReferralCodeIndividualEdit}
            create={ReferralCodeIndividualCreate}
            icon={ConnectWithoutContactIcon}
            options={{ label: "Referral Code" }}
          /> */}
          {/*<Resource
            name="referralCodeMessage"
            list={ReferralCodeListMessage}
            edit={ReferralCodeMessageEdit}
            create={ReferralCodeMessageCreate}
            icon={ConnectWithoutContactIcon}
            options={{ label: "Referral Message" }}
          />*/}
          <Resource
            name="emailProductDetailText"
            list={EmailProductDetailTextList}
            edit={EmailProductDetailTextEdit}
            create={EmailProductDetailTextCreate}
            icon={EmailIcon}
            options={{ label: "Email Products" }}
          />
          <Resource
            name="emailTemplates"
            list={EmailTemplatesList}
            edit={EmailTemplatesEdit}
            create={EmailTemplatesCreate}
            recordRepresentation={(record) => `${record.emailType}`}
            icon={EmailIcon}
            options={{ label: "Email Templates" }}
            hasShow={false}
            hasCreate={false}
          />
          <Resource
            name="firebaseNotificationMessage"
            list={MessageList}
            create={MessageCreate}
            icon={ChatBubbleIcon}
            options={{ label: "Firebase Push Notification Messages" }}
            hasShow={false}
            hasEdit={false}
          />

          <CustomRoutes>
            <Route
              path="/notification-settings"
              element={
                <Authenticated>
                  <NotificationSettingsList />
                </Authenticated>
              }
            />
            <Route
              path="/notification-settings/:key/show"
              element={
                <Authenticated>
                  <NotificationSettingsShow />
                </Authenticated>
              }
            />
            <Route
              path="/notification-settings/:key/edit"
              element={
                <Authenticated>
                  <NotificationSettingsEdit />
                </Authenticated>
              }
            />
            <Route
              path="/firebase-notifications/:name/:appId/show"
              element={
                <Authenticated>
                  <MessageShow />
                </Authenticated>
              }
            />
            <Route
              path="/firebase-notifications/:name/show"
              element={
                <Authenticated>
                  <MessageShow />
                </Authenticated>
              }
            />
            <Route
              path="/firebase-notifications/:name/:appId/edit"
              element={
                <Authenticated>
                  <MessageEdit />
                </Authenticated>
              }
            />
            <Route
              path="/firebase-notifications/:name/edit"
              element={
                <Authenticated>
                  <MessageEdit />
                </Authenticated>
              }
            />
            <Route
              path="/sunstar-redirect-urls"
              element={
                <Authenticated>
                  <SunstarRedirectUrls />
                </Authenticated>
              }
            />
            <Route
              path="/sunstar-redirect-urls/:id/show"
              element={
                <Authenticated>
                  <SunstarRedirectUrlsShow />
                </Authenticated>
              }
            />
            <Route
              path="/sunstar-redirect-urls/:id/edit"
              element={
                <Authenticated>
                  <SunstarRedirectUrlsEdit />
                </Authenticated>
              }
            />
            <Route
              path="/settings"
              element={
                <Authenticated>
                  <Settings />
                </Authenticated>
              }
            />
            <Route
              path="/order_charts"
              element={
                <Authenticated>
                  <OrderCharts />
                </Authenticated>
              }
            />
            <Route
              path="/basket"
              element={
                <Authenticated>
                  <Basket />
                </Authenticated>
              }
            />
            <Route
              path="/invoices-archive/credit-notes"
              element={
                <Authenticated>
                  <InvoiceArchiveList />
                </Authenticated>
              }
            />
            {/* <Route path="/payment" element={<Payment />} /> */}
          </CustomRoutes>
          <Resource
            name="invoices-archive"
            list={InvoiceArchiveList}
            options={{ label: "Invoices Archive" }}
            icon={SendAndArchiveTwoTone}
          />
          <Resource name="users" list={UsersList} show={UserDetail} options={{ label: "Customers" }} icon={People} />
          <Resource name="subscriptions" list={SubscriptionList} hasShow={false} icon={CurrencyExchange} />
          <Resource
            name="freeSubscriptions"
            list={FreeSubscriptionList}
            create={FreeSubscriptionCreate}
            hasShow={false}
            icon={CardGiftcard}
            options={{ label: "Free Subscriptions" }}
          />
          <Resource
            name="shopifyOrders"
            list={ShopifyOrdersList}
            show={ShopifyOrderDetail}
            options={{ label: "Shopify Orders" }}
            icon={LocalMallIcon}
          />
          <Resource
            name="locales"
            list={LocalesList}
            show={LocaleShow}
            edit={LocaleEdit}
            create={LocaleCreate}
            options={{ label: "Countries" }}
            icon={LanguageIcon}
            recordRepresentation={(record) => record.name}
          />
          <Resource
            name="internalUserGroups"
            list={InternalUserGroupList}
            show={InternalUserGroupShow}
            edit={InternalUserGroupEdit}
            create={InternalUserGroupCreate}
            options={{ label: "Internal User Groups" }}
            icon={Groups3}
            recordRepresentation={(record) => record.name}
          />
          <Resource
            name="translationKeys"
            list={TranslationKeyList}
            show={TranslationKeyShow}
            create={TranslationKeyCreate}
            edit={TranslationKeyEdit}
            icon={KeyIcon}
            options={{ label: "Mouthcheck Translation Keys" }}
          />
          <Resource
            name="mouthscanAnalysisResults"
            list={MouthscanAnalysisResultsList}
            show={MouthscanAnalysisResultShow}
            icon={AssessmentIcon}
            options={{ label: "Mouthscan Analysis Results" }}
            hasEdit={false}
            hasCreate={false}
          />
          <Resource
            name="internalUsers"
            list={InternalUserList}
            show={InternalUserShow}
            edit={InternalUserEdit}
            options={{ label: "Internal Users" }}
            icon={AssignmentInd}
            recordRepresentation={(record) => record.name}
          />
          <Resource
            name="featureFlags"
            list={FeatureFlagsList}
            edit={FeatureFlagsEdit}
            options={{ label: "Feature Flags" }}
            icon={Flag}
          />
          <CustomRoutes noLayout>
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/checkout/:checkoutId/processing" element={<Checkout processing={true} />} />
            <Route path="/checkout/:cartId" element={<Checkout />} />
            <Route path="/checkout/:checkoutId/customer" element={<Checkout />} />
            <Route path="/checkout/:checkoutId/payment" element={<Checkout />} />
            {/* <Route path="/thank_you/:orderId" element={<ThankYou />} /> */}
            <Route path="/thank_you/:checkoutId" element={<ThankYou />} />
            <Route path="/thank_you/" element={<ThankYou />} />
            <Route path="/auth/getToken" element={<SSOLoginCallback />} />
          </CustomRoutes>
        </Admin>
      </BasketProvider>
    </StateMachineProvider>
  </BrowserRouter>
);
export default App;
